import JSZip from 'jszip'

export function readFileAsync(
  file: Blob,
  fileType: string | null = null,
): Promise<string | ArrayBuffer | null> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e: ProgressEvent<FileReader>) => resolve(e.target?.result ?? null)
    reader.onerror = (e: ProgressEvent<FileReader>) => reject(e.target?.error)
    if (fileType === null) {
      reader.readAsArrayBuffer(file)
    } else if (fileType === 'base64') {
      reader.readAsDataURL(file)
    } else {
      reader.readAsText(file)
    }
  })
}

export async function processWch(url: string): Promise<Uint8Array> {
  // 1. 下载 ZIP 文件到内存
  const response = await fetch(url, {
    mode: 'cors', // 处理跨域请求
  })
  if (!response.ok) {
    throw new Error(`下载失败: ${response.status}`)
  }
  const arrayBuffer = await response.arrayBuffer()
  return new Uint8Array(arrayBuffer)
}

export async function processWchZip(url: string): Promise<Uint8Array | null> {
  // 1. 下载 ZIP 文件到内存
  const response = await fetch(url, {
    mode: 'cors', // 处理跨域请求
  })
  if (!response.ok) {
    throw new Error(`下载失败: ${response.status}`)
  }
  const arrayBuffer = await response.arrayBuffer()
  const zip = new JSZip()
  const zipData = await zip.loadAsync(arrayBuffer)

  const entries = Object.entries(zipData.files)
  let fileUint8Array: Uint8Array | null = null
  for (const [relativePath, zipEntry] of entries) {
    if (zipEntry.dir) continue // 跳过目录
    const ext = getFileExtension(relativePath)
    if (ext !== '.bin') {
    }
    // 二进制文件：以 ArrayBuffer 读取
    const arrayBuffer = await zipEntry.async('arraybuffer')
    fileUint8Array = new Uint8Array(arrayBuffer)
    break
  }
  return fileUint8Array
}

export interface FileEntry {
  type: 'binary'
  data: Uint8Array
  extension: string
}

export async function processZip(
  url: string,
  requestInit: RequestInit = {},
): Promise<Record<string, FileEntry>> {
  try {
    // 1. 下载 ZIP 文件到内存
    const response = await fetch(url, {
      mode: 'cors', // 处理跨域请求
      ...requestInit,
    })
    if (!response.ok) {
      throw new Error(`下载失败: ${response.status}`)
    }
    const arrayBuffer = await response.arrayBuffer()

    const zip = new JSZip()
    const zipData = await zip.loadAsync(arrayBuffer)

    // 3. 存储解压结果的对象
    const fileContents: Record<string, FileEntry> = {}

    const entries = Object.entries(zipData.files)

    for (const [relativePath, zipEntry] of entries) {
      if (zipEntry.dir) continue // 跳过目录
      const ext = getFileExtension(relativePath)
      if (ext === '.hex') {
        const hexString = await zipEntry.async('string')
        fileContents[relativePath] = {
          type: 'binary',
          data: parseHexToUint8Array(hexString),
          extension: ext,
        }
      } else {
        // 二进制文件：以 ArrayBuffer 读取
        const arrayBuffer = await zipEntry.async('arraybuffer')
        fileContents[relativePath] = {
          type: 'binary',
          data: new Uint8Array(arrayBuffer),
          extension: ext,
        }
      }
    }
    return fileContents
  } catch (error) {
    console.error('处理失败:', error)
    throw error
  }
}

function getFileExtension(filename: string): string {
  return filename.slice(filename.lastIndexOf('.')).toLowerCase()
}

// 解析 Intel HEX 字符串为 Uint8Array
export function parseHexToUint8Array(hexContent: string): Uint8Array<ArrayBuffer> {
  const lines = hexContent.split(/\r?\n/)
  let hexString = ''

  for (const line of lines) {
    const trimmed = line.trim()
    if (trimmed.startsWith(':') && trimmed.length >= 11) {
      const recordType = parseInt(trimmed.substr(7, 2), 16)
      if (recordType === 0x00) {
        const dataLen = parseInt(trimmed.substr(1, 2), 16)
        const dataPart = trimmed.substr(9, dataLen * 2)
        hexString += dataPart
      }
    }
  }

  const bytes = new Uint8Array(hexString.length / 2)
  for (let i = 0; i < hexString.length; i += 2) {
    bytes[i / 2] = parseInt(hexString.substr(i, 2), 16)
  }
  return bytes
}
